import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Alert, Button, Col, Row } from "reactstrap";
import { checkImageSizeAndShrinkIfNeeded } from "../../../../utils/cameraUtils";
import { capitalizeFirstLetter, isCordCylinder } from "../scan/scanConfig";
import { useScanPipeline } from "../scan/useScanPipeline";
import { patchClaim, postClaim } from "../actions";
import Box from "./Box";
import Header from "./Header";
import ButtonSpinner from "../../../Forms/ButtonSpinner";
import ScanStepLayout from "./ScanStepLayout";

const WAITING_SECONDS = 7;

export default function MetaOCR({ title, side, meta, prevStep, nextStep }) {
    const post = useSelector(state => state.addclaim.post);
    const isSubmitting = useSelector(state => state.addclaim.isSubmitting);
    const dispatch = useDispatch();
    const webcamRef = useRef(null);

    const [focusmode, setFocusMode] = useState({ browser: [], capabilities: [], constraint: "", user_agent: "" });
    const [zoomConstraints, setZoomConstraints] = useState(false);
    const [zoomSliderValue, setZoomSliderValue] = useState(false);
    const [selectedDeviceId, setSelectedDeviceId] = useState(() =>
        localStorage.getItem('selectedCameraDeviceId') || null
    );
    const [devices, setDevices] = useState([]);
    const [countdown, setCountdown] = useState(WAITING_SECONDS * 2);
    const countdownTimerRef = useRef(null);
    const [clickCount, setClickCount] = useState(0);
    const isToggled = clickCount % 4 === 3;

    const {
        canvasRef,
        regex,
        regexParts,
        selectedRegexParts,
        setSelectedRegexParts,
        camera_results,
        jobQueue,
        processedImage,
        resetRegex,
        keep_trying_or_click_image,
        take_picture_and_continue,
        labels,
    } = useScanPipeline({ side, meta, webcamRef });

    const hasImage = post.images?.[side]?.base64 || post.images?.[side]?.url;
    const regexImageNeeded = meta?.use_OCR;

    const selectedDeviceIdExists = devices.find(d => d.deviceId === selectedDeviceId);
    const videoConstraints = useMemo(() => ({
        facingMode: "environment",
        focusMode: "continuous",
        width: 960,
        height: 720,
        ...(selectedDeviceIdExists && { deviceId: { exact: selectedDeviceId } }),
    }), [selectedDeviceIdExists, selectedDeviceId]);

    useEffect(() => {
        countdownTimerRef.current = setInterval(() => {
            setCountdown(prev => (hasImage || !regexImageNeeded) ? WAITING_SECONDS * 2 : Math.max(prev - 1, 0));
        }, 500);
        return () => clearInterval(countdownTimerRef.current);
    }, [hasImage, regexImageNeeded]);

    const goBack = useCallback(() => dispatch(patchClaim({ step: prevStep })), [dispatch, prevStep]);
    const goNext = useCallback(() => dispatch(patchClaim({ step: nextStep })), [dispatch, nextStep]);

    const capture = useCallback(async () => {
        if (countdown > 1 && regexImageNeeded && regex.filter(i => !i.result).length > 0) {
            toast.info("Please point your camera at the label and try for a minimum of 8 seconds.", { autoClose: 2500 });
            return;
        }
        const video = webcamRef.current?.video;
        if (video) {
            const base64 = webcamRef.current.getScreenshot();
            const resizedBase64 = await checkImageSizeAndShrinkIfNeeded(base64, video.videoWidth, video.videoHeight);
            const images = post.images ? { ...post.images } : {};
            images[side] = { base64: resizedBase64, processed: false, focusmode };
            dispatch(postClaim({ ...post, images }));
            dispatch(postClaim({ ...post, images }, true));
        }
    }, [countdown, regexImageNeeded, regex, post, side, dispatch, focusmode]);

    const removeImage = useCallback(() => {
        const images = { ...post.images, [side]: false };
        dispatch(postClaim({ ...post, images }));
    }, [post, side, dispatch]);

    const onCameraError = useCallback((error) => {
        console.error("onCameraError", error);
        toast.error("Failed to start camera. Please stop any application that is using your camera.", {
            toastId: "create-location",
            autoClose: 3500,
        });
    }, []);

    const onUserMedia = useCallback((media) => {
        const videoTracks = media.getVideoTracks();
        let track = selectedDeviceId ? videoTracks.find(t => t.getSettings()?.deviceId === selectedDeviceId) : null;
        if (!track && videoTracks.length > 0) track = videoTracks[0];
        if (!track) return;
        const capabilities = track.getCapabilities?.();
        const settings = track.getSettings?.();
        navigator.mediaDevices.enumerateDevices().then(mediaDevices =>
            setDevices(mediaDevices.filter(d => d.kind === "videoinput"))
        );
        setFocusMode({
            browser: navigator.mediaDevices?.getSupportedConstraints?.()?.focusMode || false,
            capabilities: capabilities?.focusMode || [],
            constraint: settings?.focusMode || "",
            user_agent: navigator.userAgent,
        });
        if (capabilities?.zoom && settings?.zoom && zoomConstraints === false) {
            setZoomConstraints({ ...capabilities.zoom, value: settings.zoom });
            setZoomSliderValue(settings.zoom);
        } else {
            setZoomConstraints(false);
        }
    }, [selectedDeviceId, zoomConstraints]);

    const setZoomSliderValueConstraint = useCallback((value) => {
        setZoomSliderValue(value);
        const videoTracks = webcamRef.current?.video?.srcObject?.getVideoTracks();
        let videoTrack = selectedDeviceId && videoTracks ? videoTracks.find(t => t.getSettings()?.deviceId === selectedDeviceId) : null;
        if (!videoTrack && videoTracks?.length > 0) videoTrack = videoTracks[0];
        if (videoTrack?.getCapabilities?.().zoom) {
            videoTrack.applyConstraints({ advanced: [{ zoom: parseFloat(value) }] }).catch(console.error);
        }
    }, [selectedDeviceId]);

    const handleClick = () => setClickCount(prev => (prev === 3 ? 0 : prev + 1));

    const item_number = post.item_number ? "item-" + post.item_number.replace(/\./g, "-") : "label";

    const instructions = useMemo(() => {
        const selectDevice = devices.length > 1 ? <span> If the camera view is not clear or distorted, please select another camera. </span> : null;
        if (take_picture_and_continue) {
            return <Alert color="info" className="mb-3">Take a picture of the whole item and continue! {selectDevice}</Alert>;
        }
        if (meta?.use_OCR) {
            if (isCordCylinder(post.item_number)) {
                if (keep_trying_or_click_image) {
                    return <Alert color="warning" className="mb-3">Keep trying to scan or take a picture of the QR/Datamatrix code to continue. {selectDevice}</Alert>;
                }
                return <Alert color="info" className="mb-3">Please align the black L-shape with the QR/Datamatrix code. {selectDevice}</Alert>;
            }
            if (keep_trying_or_click_image) {
                return <Alert color="warning" className="mb-3">Keep trying to scan the {meta.name} or take a picture of the {meta.name} to continue. {selectDevice}</Alert>;
            }
            return <Alert color="info" className="mb-3">Please point the camera directly at the {meta.name} and try to scan the needed information. {selectDevice}</Alert>;
        }
        return <Alert color="info" className="mb-3">Please point the camera directly at the item and try to scan the needed information. {selectDevice}</Alert>;
    }, [keep_trying_or_click_image, take_picture_and_continue, meta, post.item_number, devices.length]);

    const hasValidImage = post.images?.[side]?.url || post.images?.[side]?.id;

    const results = regex.map((i, index) => (
        <div key={index} className="pt-2">
            {isToggled && <i>{String(i.regex)}<br /></i>}
            <div onClick={handleClick}><b>{capitalizeFirstLetter(labels?.[i.field]?.label || i.field)}</b></div>
            {i.result && (
                <span className="text-success" onClick={() => resetRegex(i)}>
                    <Button color="light">{i.result} <span className="text-danger mx-1"><FontAwesomeIcon icon={["far", "times"]} /></span></Button>
                    <b>Take a picture and continue!</b>
                </span>
            )}
        </div>
    ));

    if (hasImage) {
        return (
            <Box>
                <Header name={title} />
                <div>
                    <div className="position-relative">
                        <div className="ocr-camera__toggle">
                            <Button color="secondary" onClick={removeImage} size="sm"><FontAwesomeIcon icon={faTimes} /></Button>
                        </div>
                        <img className="d-block h-100 w-100" alt={`Item ${side}`} src={post.images[side].url || post.images[side].base64} onClick={removeImage} />
                    </div>
                    <Row className="pt-3">
                        <Col><Button color="secondary" className="w-100" onClick={goBack}>Back</Button></Col>
                        <Col>
                            <ButtonSpinner loading={isSubmitting} color="primary" className="w-100" onClick={goNext} disabled={!hasValidImage}>Next</ButtonSpinner>
                        </Col>
                    </Row>
                    {post.traceback && (
                        <Row className="pt-3 opacity-30">
                            <Col><span className="text-small text-muted">Traceback code: {post.traceback}</span></Col>
                        </Row>
                    )}
                </div>
            </Box>
        );
    }

    const zoomSlider = zoomConstraints ? {
        min: zoomConstraints.min,
        max: zoomConstraints.max,
        value: zoomSliderValue,
        onChange: setZoomSliderValueConstraint,
    } : null;

    const regexPickers = (Object.keys(regexParts.AB || {}).length > 0 || Object.keys(regexParts.the_number || {}).length > 0)
        ? { regexParts, selectedRegexParts, setSelectedRegexParts }
        : null;

    return (
        <ScanStepLayout
            title={title}
            instructions={instructions}
            side={side}
            itemNumber={item_number}
            devices={devices}
            selectedDeviceId={selectedDeviceId}
            onDeviceChange={setSelectedDeviceId}
            webcamRef={webcamRef}
            videoConstraints={videoConstraints}
            onCameraError={onCameraError}
            onUserMedia={onUserMedia}
            processedImage={processedImage}
            isToggled={isToggled}
            countdown={countdown}
            showProgress={regexImageNeeded}
            waitingSeconds={WAITING_SECONDS}
            onOverlayClick={capture}
            cordCylinder={isCordCylinder(post.item_number) && regexImageNeeded}
            zoomSlider={zoomSlider}
            regexPickers={regexPickers}
            results={results}
            jobQueue={jobQueue}
            camera_results={camera_results}
            canvasRef={canvasRef}
            goBack={goBack}
            goNext={goNext}
            nextDisabled={!hasValidImage}
            nextLoading={isSubmitting}
            traceback={post.traceback}
        />
    );
}
